import { useEffect, useState } from "react";
import { useCompanyFeature } from "hooks/useCompanyFeature";
import axios from "axios";
import { Button } from "components/button";
import { toast } from "react-toastify";

interface IInvoiceItem {
    lineNumber: string;
    itemDescription: string;
    quantityInvoiced: string;
    salesPrice: string;
}

interface IInvoice {
    invoiceNumber: string;
    invoiceDate: string;
    invoiceTotal: number;
    taxTotals: number;
    otherCharges: number;
    invoiceItems: IInvoiceItem[];
}

const InvoiceStatus = ({ orderId }: { orderId: number }) => {
    // Feature off: the endpoint 404s; render nothing.
    const heritageEnabled = useCompanyFeature("heritage");
    const [open, setOpen] = useState(false);
    const [invoice, setInvoice] = useState<IInvoice | null>(null);
    const [loading, setLoading] = useState(false);

    const fetchInvoice = async () => {
        try {
            setLoading(true);
            const res = await axios.get(
                `${process.env.REACT_APP_SERVER_URL}/api/v1/company/heritage/invoice-status`,
                { params: { orderId } }
            );



            if (res.data?.invoiceNumber) {
                setInvoice(res.data);
                toast.success("Invoice data refreshed");
            } else {
                setInvoice(null);
                toast.info("No invoice found");
            }
        } catch (err) {
            console.error("Failed to fetch invoice", err);
            toast.error("Failed to fetch invoice data");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open && heritageEnabled) fetchInvoice();
    }, [open, heritageEnabled]);

    if (!heritageEnabled) return null;

    return (
        <>
            <Button className="ml-2" onClick={() => setOpen(true)}>
                Invoice
            </Button>

            {open && (
                <div className="fixed inset-0 z-50 bg-black bg-opacity-30 flex items-center justify-center">
                    <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl p-6 relative">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-lg font-semibold">Invoice Details</h2>
                            <button
                                className="text-gray-500 hover:text-gray-700"
                                onClick={() => setOpen(false)}
                            >
                                ✕
                            </button>
                        </div>

                        <div className="mb-4">
                            <Button onClick={fetchInvoice}>
                                Refresh
                            </Button>
                        </div>

                        {loading ? (
                            <div className="text-sm text-gray-500">Loading invoice data...</div>
                        ) : invoice ? (
                            <>
                                <div className="bg-gray-50 border rounded p-4 text-sm space-y-1 mb-4">
                                    <div><strong>Invoice #:</strong> {invoice.invoiceNumber}</div>
                                    <div><strong>Date:</strong> {invoice.invoiceDate}</div>
                                    <div><strong>Total:</strong> ${invoice.invoiceTotal.toFixed(2)}</div>
                                    <div><strong>Tax:</strong> ${invoice.taxTotals.toFixed(2)}</div>
                                    <div><strong>Other Charges:</strong> ${invoice.otherCharges.toFixed(2)}</div>
                                </div>

                                <table className="min-w-full bg-white text-sm border rounded">
                                    <thead>
                                        <tr>
                                            <th className="text-left py-2 px-4 border-b">Line</th>
                                            <th className="text-left py-2 px-4 border-b">Description</th>
                                            <th className="text-left py-2 px-4 border-b">Qty</th>
                                            <th className="text-left py-2 px-4 border-b">Price</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {invoice.invoiceItems.map((item, i) => (
                                            <tr key={i}>
                                                <td className="py-2 px-4 border-b">{item.lineNumber}</td>
                                                <td className="py-2 px-4 border-b">{item.itemDescription}</td>
                                                <td className="py-2 px-4 border-b">{item.quantityInvoiced}</td>
                                                <td className="py-2 px-4 border-b">${item.salesPrice}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </>
                        ) : (
                            <div className="text-sm text-gray-500">No invoice found for this order.</div>
                        )}
                    </div>
                </div>
            )}
        </>
    );
};

export default InvoiceStatus;
